import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  getAccountDetailsAppAccountsAccountsAccountIdGetOptions,
  getAccountHistoryAppAccountsHistoryAccountIdGetOptions,
} from '@/client/@tanstack/react-query.gen'
import type { TimelineEventType } from '@/lib/event-formatting'
import {
  formatEventType,
  getExternalEventDate,
} from '@/lib/event-formatting'
import { SkeletonList } from '@/components/skeleton-common'
import { ScrollArea } from '@/components/ui/scroll-area'
import { TabsContent } from '@/components/ui/tabs'
import { toDate } from 'date-fns'
import { Button } from '@/components/ui/button'
import { TimelineItem } from '../overview/-components/account-history/timeline-item'
import { NoteInput } from './-components/note-input'
import { DEFAULT_HISTORY_FILTER_STATE, HistoryFilterPopover, isDefaultHistoryFilterState } from './-components/history-filter-popover'
import type { HistoryFilterState } from './-components/history-filter-popover'
import { useAuth } from '@/hooks/use-auth'
import { canCreateAccountActivity } from '../-utils/account-permissions'
import {
  getHistoryFilterStorageKey,
  readHistoryFilterStorageValue,
  writeHistoryFilterStorageValue,
} from './-utils/history-filter-persistence'
import type { HistoryFilterStorageSnapshot } from './-utils/history-filter-persistence'

export const Route = createFileRoute(
  '/_authed/_user/accounts/$accountId/history/',
)({
  component: AccountHistoryTab,
})

function HistoryContent({
  canAddNote,
  hasUnfilteredEvents,
  sortedEvents,
}: {
  canAddNote: boolean
  hasUnfilteredEvents: boolean
  sortedEvents: TimelineEventType[]
}) {
  if (sortedEvents.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        {hasUnfilteredEvents
          ? 'No history matches these filters.'
          : (canAddNote ? 'No history yet. Add a note to get started.' : 'No history yet.')}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {sortedEvents.map(event => (
        <TimelineItem key={`history-${event.isExternal ? 'ext' : 'int'}-${event.id}`} event={event} />
      ))}
    </div>
  )
}

function AccountHistoryTab() {
  const { accountId } = Route.useParams()
  const { currentUserRole, isSupplycoStaff, user } = useAuth()
  const historyFilterStorageKey = getHistoryFilterStorageKey(user?.id)
  const [historyFilterStorageSnapshot, setHistoryFilterStorageSnapshot] = useState<HistoryFilterStorageSnapshot>(
    () => ({
      key: historyFilterStorageKey,
      state: readHistoryFilterStorageValue(historyFilterStorageKey),
    }),
  )
  const historyFilterState = historyFilterStorageSnapshot.key === historyFilterStorageKey
    ? historyFilterStorageSnapshot.state
    : readHistoryFilterStorageValue(historyFilterStorageKey)
  const setHistoryFilterState = (state: HistoryFilterState) => {
    writeHistoryFilterStorageValue(historyFilterStorageKey, state)
    setHistoryFilterStorageSnapshot({ key: historyFilterStorageKey, state })
  }

  const { data: accountDetails } = useQuery(
    getAccountDetailsAppAccountsAccountsAccountIdGetOptions({
      path: { account_id: accountId },
    }),
  )

  const {
    data: historyData,
    isLoading,
  } = useQuery(
    getAccountHistoryAppAccountsHistoryAccountIdGetOptions({
      path: { account_id: accountId },
    }),
  )

  const companyEvents = historyData?.companyEvents ?? []
  const accountActions = historyData?.accountActions ?? []

  const internalEvents: TimelineEventType[] = accountActions.map(action => ({
    ...action,
    isExternal: false as const,
  }))
  const externalEvents: TimelineEventType[] = companyEvents.map(event => ({
    ...event,
    isExternal: true as const,
  }))

  const allEvents: TimelineEventType[] = [...externalEvents, ...internalEvents]
  const marketEventTypeCounts = new Map<string, number>()
  for (const event of companyEvents) {
    marketEventTypeCounts.set(event.eventName, (marketEventTypeCounts.get(event.eventName) ?? 0) + 1)
  }
  const marketEventTypes = [...marketEventTypeCounts.keys()].toSorted((a, b) =>
    formatEventType(a).localeCompare(formatEventType(b)),
  )
  const selectedMarketEventTypes = new Set(historyFilterState.marketEventTypes)
  const filteredEvents = allEvents.filter((event) => {
    if (!event.isExternal) {
      return historyFilterState.showUserActivity
    }

    if (!historyFilterState.showMarketData) {
      return false
    }

    return selectedMarketEventTypes.size === 0 || selectedMarketEventTypes.has(event.eventName)
  })
  const sortedEvents = filteredEvents.toSorted((a, b) => {
    const dateA = a.isExternal ? getExternalEventDate(a) : a.createdAt
    const dateB = b.isExternal ? getExternalEventDate(b) : b.createdAt

    if (!dateA || dateA === '')
      return 1
    if (!dateB || dateB === '')
      return -1

    return toDate(dateB).getTime() - toDate(dateA).getTime()
  })
  const canAddNote = canCreateAccountActivity({
    accountDetails,
    currentUserRole,
    isSupplycoStaff,
  })

  return (
    <TabsContent value="history" className="flex-1">
      <div className="flex h-full w-full flex-col gap-4 px-4 lg:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <HistoryFilterPopover
            state={historyFilterState}
            onChange={setHistoryFilterState}
            marketEventTypes={marketEventTypes}
            userActivityCount={accountActions.length}
            marketDataCount={companyEvents.length}
            getMarketEventTypeCount={eventType => marketEventTypeCounts.get(eventType) ?? 0}
          />
          <div className="flex-1" />
          <span className="text-sm text-muted-foreground shrink-0">
            {sortedEvents.length.toLocaleString()}
            {' '}
            {sortedEvents.length === 1 ? 'event' : 'events'}
          </span>
          {!isDefaultHistoryFilterState(historyFilterState) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setHistoryFilterState(DEFAULT_HISTORY_FILTER_STATE)}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {canAddNote && <NoteInput accountId={accountId} />}

        <ScrollArea className="flex-1">
          {isLoading
            ? <SkeletonList items={6} className="py-4" />

            : (
                <HistoryContent
                  canAddNote={canAddNote}
                  hasUnfilteredEvents={allEvents.length > 0}
                  sortedEvents={sortedEvents}
                />
              )}
        </ScrollArea>
      </div>
    </TabsContent>
  )
}
